import React, { ReactNode } from 'react';
import { CheckIcon, ChevronRightIcon, DotsVerticalIcon } from '@radix-ui/react-icons';
import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import { CheckboxMenuItem, IconButton, ListItem, OptionLabel, SubTrigger, Tooltip } from '@producer/ui-kit';

import type { CheckboxItem, Item } from '@producer/ui-kit';

import styles from './dropdown-menu.module.scss';

// The item model now lives in @producer/ui-kit; re-exported here so the existing
// importers of these two types keep resolving.

export type { CheckboxItem, Item } from '@producer/ui-kit';

type DropdownMenuProps = {
  items: Array<Item | CheckboxItem>;
  align?: 'start' | 'end';
  size?: 'small' | 'medium' | 'large' | 'free-size';
  trigger?: ReactNode;
  itemComponent?: React.ReactElement;
  tooltipText?: string;
  modal?: boolean;
  onOpenChange?: (value: boolean) => void;
  portalContainer?: HTMLDivElement;
  side?: 'bottom' | 'top';
  disabled?: boolean;
};

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  items,
  size,
  trigger,
  itemComponent: optionComponent,
  align = 'end',
  modal = false,
  onOpenChange,
  portalContainer,
  side,
  disabled,
}) => {
  const hasIcon = items.some((item) => !!item.icon || !!item.onCheckedChange);
  const hasIconAndCheck = items.some((item) => !!item.icon && !!item.onCheckedChange);

  const renderItem = (item: Item | CheckboxItem, index: number, hasIcon?: boolean) => {
    if (item.hidden) {
      return;
    }

    if (optionComponent && React.isValidElement(optionComponent)) {
      return React.cloneElement(optionComponent, { ...item });
    }

    if (hasIconAndCheck) {
      return (
        <CheckboxMenuItem
          key={`option-${index}`}
          checked={item.isChecked}
          onCheckedChange={item.onCheckedChange}
          onSelect={item.onSelect}
        >
          <OptionLabel>
            {item.icon}

            <span>{item.title}</span>
          </OptionLabel>

          <RadixMenu.ItemIndicator>
            <CheckIcon />
          </RadixMenu.ItemIndicator>
        </CheckboxMenuItem>
      );
    }

    if (item.onCheckedChange) {
      return (
        <CheckboxMenuItem
          key={`option-${index}`}
          checked={item.isChecked}
          onCheckedChange={item.onCheckedChange}
          onSelect={item.onSelect}
        >
          <OptionLabel>
            {item.icon}

            <span>{item.title}</span>
          </OptionLabel>

          <RadixMenu.ItemIndicator>
            <CheckIcon />
          </RadixMenu.ItemIndicator>
        </CheckboxMenuItem>
      );
    }

    if (item.customComponent) {
      return React.cloneElement(item.customComponent, { ...item });
    }

    return (
      <ListItem
        key={`option-${index}`}
        disabled={item.disabled}
        onSelect={item.onSelect}
        data-type={item.type}
      >
        <OptionLabel>
          {hasIcon && item.icon}

          <span>{item.title}</span>
        </OptionLabel>
      </ListItem>
    );
  };

  return (
    <RadixMenu.Root modal={modal} onOpenChange={onOpenChange}>
      <RadixMenu.Trigger onClick={(e) => e.stopPropagation()} disabled={disabled} asChild>
        {trigger || <IconButton data-size={size} icon={<DotsVerticalIcon />} />}
      </RadixMenu.Trigger>

      <RadixMenu.Portal {...(portalContainer && { container: portalContainer })}>
        <RadixMenu.Content className={styles.content} side={side || 'bottom'} align={align} sideOffset={6} asChild>
          <div
            className={styles.menu}
            data-size={size}
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((item, index) => {
              const hasSubIcon = item.subItems?.some(
                (item) => !!item.icon || !!item.onCheckedChange,
              );

              return (
                <Tooltip
                  text={item.tooltipText || 'Action not allowed'}
                  key={`tip-${index}`}
                  disabled={!item.disabled || !item.tooltipText}
                >
                  {item.subItems ? (
                    <RadixMenu.Sub key={`sub-trigger-${index}`}>
                      <SubTrigger disabled={item.disabled}>
                        {hasIcon && item.icon}

                        <span>{item.title}</span>

                        <span className={styles.slot}>
                          <ChevronRightIcon />
                        </span>
                      </SubTrigger>

                      <RadixMenu.SubContent className={styles.sub} sideOffset={4}>
                        {item.subItems.map((subOption, subOptionIndex) =>
                          renderItem(subOption, subOptionIndex, hasSubIcon),
                        )}
                      </RadixMenu.SubContent>
                    </RadixMenu.Sub>
                  ) : (
                    renderItem(item, index, hasIcon)
                  )}
                </Tooltip>
              );
            })}
          </div>
        </RadixMenu.Content>
      </RadixMenu.Portal>
    </RadixMenu.Root>
  );
};
